import { AuthAuthPermissions } from '@robovision/rvai-auth'
import type { RvaiAbilityBuilder } from '../types'


export function defineProjectAbility (
  builder: RvaiAbilityBuilder,
  permissions: AuthAuthPermissions[],
): void {
  const {
    can,
    cannot,
  } = builder

  can('access', 'Projects')
  can('read', 'Algorithm')

  if (permissions.some(p => p === AuthAuthPermissions.SYSTEM_MANAGEMENT)) {
    can('crud', 'Algorithm')
  }

  if (permissions.some(p => p === AuthAuthPermissions.DATA_LABELING) &&
    can('crud', 'Annotation') &&
    can('read', 'Class')) {
    can('read', 'Tag')
  }

  if (permissions.some(p => p === AuthAuthPermissions.DATA_UPLOAD)) {
    can('read', 'Annotation')
    can('crud', 'Class')
    can('crud', 'Import')
    can('read', 'Project')
    can('crud', 'Recording')
  }


  if (permissions.some(p => p === AuthAuthPermissions.INFERENCE)) {
    can('read', 'Dataset')
    can('crud', 'Inference')
    can('read', 'Project')
    can('read', 'Model')
  }

  if (permissions.some(p => p === AuthAuthPermissions.LABELING_REVIEW)) {
    can('crud', 'Annotation')
    can('read', 'Class')
    can('crud', 'Tag')
    can('access', 'Charts')
    can('crud', 'Review')
  }

  if (permissions.some(p => p === AuthAuthPermissions.PROJECT_CREATION)) {
    can('crud', 'Project')
    can('delete', 'Model')
  }

  if (permissions.some(p => p === AuthAuthPermissions.MODEL_CREATION)) {
    can('read', 'Annotation')
    can('read', 'Class')
    can('crud', 'Dataset')
    can('read', 'Project')
    can('crud', 'Test')
    can('crud', 'Tag')
    can('crud', 'Model')
    can('access', 'Charts')
  }


  if (permissions.length === 1) {
    const [permission] = permissions
    if (permission === AuthAuthPermissions.SYSTEM_MANAGEMENT) {
      // If user has only SYSTEM_MANAGEMENT permission he can use projects in readonly mode
      cannot('access', 'Projects')
    }
    if (permission === AuthAuthPermissions.PROJECT_CREATION) {
      // If user has only PROJECT_CREATION permission he shouldn't be able to open project details
      cannot('read', 'Project')
    }
    if (permission === AuthAuthPermissions.DATA_LABELING) {
      // If user has only DATA_LABELING permission he shouldn't be able to access other users annotations
      cannot('read', 'Annotation')
    }
  }

  const deleteProjectPermissionCombinations = [
    [
      AuthAuthPermissions.DATA_UPLOAD,
      AuthAuthPermissions.INFERENCE,
      AuthAuthPermissions.PROJECT_CREATION,
      AuthAuthPermissions.SYSTEM_MANAGEMENT,
    ],
    [
      AuthAuthPermissions.INFERENCE,
      AuthAuthPermissions.PROJECT_CREATION,
      AuthAuthPermissions.MODEL_CREATION,
    ],
    [
      AuthAuthPermissions.MODEL_CREATION,
      AuthAuthPermissions.PROJECT_CREATION,
      AuthAuthPermissions.SYSTEM_MANAGEMENT,
    ],
  ]

  // Prohibit project deletion if user has none of combinations specified above
  if (!deleteProjectPermissionCombinations.some(
    combination => combination.every(permission => permissions.includes(permission)),
  )) {
    cannot('delete', 'Project')
  }
}
